/**
 * A client's tax input sheet for one year: every figure in Form 1040 order with where it came
 * from, carryovers the preparer adds, and the exports for the tax software (the package for
 * OLT Pro's importer, the worksheet PDF, the CSV and the structured file).
 */
import { yearChoices } from "@clientflow/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertTriangle, Download, Eye, EyeOff, FileText, Package, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { Empty, Loading, PageHeader, Section } from "@/components/page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errorMessage, queryClient } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { hasPermission, useMe } from "@/lib/session";
import { cn } from "@/lib/utils";

type Source =
  | { kind: "document"; readId: string; documentId: string; filename: string; confirmedBy: string | null; confirmedAt: string | null }
  | { kind: "organizer"; organizerId: string; title: string; status: string }
  | { kind: "entry"; entryId: string; by: string | null; note: string; at: string };
type Sheet = {
  clientId: string;
  clientName: string;
  taxYear: number;
  revealed: boolean;
  sections: Array<{ key: string; title: string; groups: Array<{ title: string; source: Source; lines: Array<{ code: string; label: string; value: string; cents: number | null }> }> }>;
  pending: { toReview: number; openRequests: Array<{ id: string; title: string }> };
  carryoverItems: Array<{ code: string; label: string; kind: "money" | "text" }>;
};

const select = "block h-9 w-full rounded-md border border-input bg-background px-2 text-sm";

export function TaxInputPage({ clientId }: { clientId: string }) {
  const search = new URLSearchParams(useSearch());
  const [, go] = useLocation();
  const today = new Date().toISOString().slice(0, 10);
  // The year asked for; otherwise the latest with anything on it, or last year.
  const { data: had, isFetched: yearsKnown } = useQuery<{ years: number[] }>({ queryKey: [`/api/clients/${clientId}/tax-input/years`] });
  const year = Number(search.get("year")) || had?.years[0] || Number(today.slice(0, 4)) - 1;
  const [reveal, setReveal] = useState(false);
  const { data: me } = useMe();
  const preparer = hasPermission(me, "engagements.write");
  const key = `/api/clients/${clientId}/tax-input?taxYear=${year}${reveal ? "&reveal=1" : ""}`;
  // Full numbers aren't kept, and aren't fetched again just because the window regains focus.
  const { data, error } = useQuery<Sheet>({ queryKey: [key], enabled: yearsKnown || !!search.get("year"), gcTime: reveal ? 0 : undefined, refetchOnWindowFocus: !reveal });
  const q = `taxYear=${year}${reveal ? "&reveal=1" : ""}`;
  const years = Array.from(new Set([...(had?.years ?? []), ...yearChoices(today, 2018).map((y) => y - 1)]))
    .filter((y) => y >= 2018)
    .sort((a, b) => b - a);

  return (
    <div>
      <PageHeader
        title={`${year} tax input sheet`}
        crumbs={[{ label: "Clients", href: "/clients" }, { label: data?.clientName ?? "Client", href: `/clients/${clientId}?tab=tax` }, { label: "Tax input sheet" }]}
        description="Every figure for the return, in the order of Form 1040 and its schedules, with the document or answer it came from and who confirmed it."
        actions={
          <div className="flex items-center gap-2">
            <Label htmlFor="ti-year" className="mb-0 text-sm text-muted-foreground">
              Tax year
            </Label>
            <select id="ti-year" className={cn(select, "w-28")} value={year} onChange={(e) => go(`/clients/${clientId}/tax-input?year=${e.target.value}`)}>
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        }
      />
      {error ? (
        <p className="text-sm text-destructive">{errorMessage(error)}</p>
      ) : !data ? (
        <Loading />
      ) : (
        <div className="grid min-w-0 gap-5 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
          <div className="min-w-0 space-y-5">
            {(data.pending.toReview > 0 || data.pending.openRequests.length > 0) && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700/50 dark:bg-amber-950/30">
                <p className="mb-1 flex items-center gap-1.5 font-semibold">
                  <AlertTriangle className="h-4 w-4 text-amber-600" aria-hidden /> Not final yet
                </p>
                <ul className="list-disc space-y-0.5 pl-5">
                  {data.pending.toReview > 0 && (
                    <li>
                      {data.pending.toReview} uploaded document{data.pending.toReview === 1 ? " is" : "s are"} waiting in{" "}
                      <Link href={`/documents/review?clientId=${clientId}`} className="font-medium text-primary hover:underline">
                        Review › Documents
                      </Link>
                      .
                    </li>
                  )}
                  {data.pending.openRequests.slice(0, 8).map((r) => (
                    <li key={r.id}>Still requested: {r.title}</li>
                  ))}
                  {data.pending.openRequests.length > 8 && <li>and {data.pending.openRequests.length - 8} more requests</li>}
                </ul>
              </div>
            )}
            {data.sections.length === 0 ? (
              <Empty>No figures for {year} yet. They appear as documents are confirmed in Review › Documents and the organizer is answered.</Empty>
            ) : (
              data.sections.map((sec) => (
                <Section key={sec.key} title={sec.title}>
                  <div className="space-y-5">
                    {sec.groups.map((g, i) => (
                      <Group key={i} group={g} canEdit={preparer} />
                    ))}
                  </div>
                </Section>
              ))
            )}
          </div>

          <div className="min-w-0 space-y-5 lg:sticky lg:top-4">
            {preparer && (
              <Section title="Export for the tax software">
                <div className="space-y-2">
                  <a href={`/api/clients/${clientId}/tax-input/package.zip?${q}`} className="flex items-center gap-2.5 rounded-md bg-primary px-3 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90">
                    <Package className="h-4 w-4 shrink-0" aria-hidden /> Package for OLT Pro (zip)
                  </a>
                  <p className="text-xs text-muted-foreground">The worksheet, each source document named for OLT Pro's W-2/1099 import tool, the CSV and the structured file.</p>
                  <div className="grid gap-1.5 pt-1">
                    <ExportLink href={`/api/clients/${clientId}/tax-input/worksheet.pdf?${q}`} label="Worksheet with source documents (PDF)" />
                    <ExportLink href={`/api/clients/${clientId}/tax-input/export.csv?${q}`} label="Every figure (CSV)" />
                    <ExportLink href={`/api/clients/${clientId}/tax-input/export.json?${q}`} label="Structured file with codes (JSON)" />
                  </div>
                  <Button variant="ghost" size="sm" className="mt-1" onClick={() => setReveal((r) => !r)}>
                    {reveal ? <EyeOff className="mr-1.5 h-4 w-4" /> : <Eye className="mr-1.5 h-4 w-4" />}
                    {reveal ? "Mask protected numbers" : "Show protected numbers"}
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    {reveal ? "Protected numbers are shown and included in downloads. Each view and download is recorded." : "On the sheet and in its downloads, Social Security numbers, EINs and bank account numbers show their last four digits. Source documents are included unchanged."}
                  </p>
                </div>
              </Section>
            )}
            {preparer && <AddEntry clientId={clientId} year={year} items={data.carryoverItems} />}
          </div>
        </div>
      )}
    </div>
  );
}

function ExportLink({ href, label }: { href: string; label: string }) {
  return (
    <a href={href} className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted">
      <Download className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden /> {label}
    </a>
  );
}

function Group({ group: g, canEdit }: { group: Sheet["sections"][number]["groups"][number]; canEdit: boolean }) {
  const remove = useMutation({
    meta: { inlineError: true },
    mutationFn: (id: string) => api.send("DELETE", `/api/tax-input/entries/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ predicate: (x) => String(x.queryKey[0]).includes("/tax-input") }),
  });
  const src = g.source;
  return (
    <div className="min-w-0">
      <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <p className="text-sm font-semibold">{g.title}</p>
        <p className="text-xs text-muted-foreground">
          {src.kind === "document" ? (
            <>
              <FileText className="mr-1 inline h-3.5 w-3.5" aria-hidden />
              <Link href={`/documents/review?id=${src.readId}&tab=confirmed`} className="hover:underline">
                {src.filename}
              </Link>
              {src.confirmedBy ? ` · confirmed by ${src.confirmedBy}` : ""}
              {src.confirmedAt ? ` on ${formatDate(src.confirmedAt)}` : ""}
            </>
          ) : src.kind === "organizer" ? (
            `From the organizer${src.status === "submitted" ? "" : " (not yet submitted)"}`
          ) : (
            <>
              Entered by {src.by ?? "staff"} on {formatDate(src.at)}
              {canEdit && (
                <button type="button" aria-label={`Remove ${g.title}`} className="ml-2 inline-flex align-middle text-muted-foreground hover:text-destructive" onClick={() => remove.mutate(src.entryId)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </>
          )}
        </p>
      </div>
      {src.kind === "entry" && src.note && <p className="mb-1.5 text-xs text-muted-foreground">{src.note}</p>}
      {remove.error && <p className="mb-1.5 text-xs text-destructive">{errorMessage(remove.error)}</p>}
      {g.lines.length === 0 ? (
        <p className="text-xs text-muted-foreground">No figures were recorded for this document.</p>
      ) : (
        <dl className="divide-y divide-border rounded-md border border-border text-sm">
          {g.lines.map((l) => (
            <div key={l.code} className="grid gap-1 px-3 py-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-baseline sm:gap-4">
              <dt className="min-w-0">
                <span className="text-muted-foreground">{l.label}</span>
                <span className="ml-2 font-mono text-[10px] text-muted-foreground">{l.code}</span>
              </dt>
              <dd className={cn("font-medium sm:text-right", l.cents !== null && "tabular")}>{l.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

function AddEntry({ clientId, year, items }: { clientId: string; year: number; items: Sheet["carryoverItems"] }) {
  const [kind, setKind] = useState<"carryovers" | "other">("carryovers");
  const [code, setCode] = useState(items[0]?.code ?? "");
  const [label, setLabel] = useState("");
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");
  const add = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send("POST", `/api/clients/${clientId}/tax-input/entries`, { taxYear: year, section: kind, code: kind === "carryovers" ? code : undefined, label: kind === "carryovers" ? (items.find((i) => i.code === code)?.label ?? "Carryover") : label, value, note }),
    onSuccess: () => {
      setValue("");
      setNote("");
      setLabel("");
      void queryClient.invalidateQueries({ predicate: (x) => String(x.queryKey[0]).includes("/tax-input") });
    },
  });
  return (
    <Section title="Add to the sheet" description="Carryovers from last year's return, or any figure no document carries.">
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          add.mutate();
        }}
      >
        <div>
          <Label htmlFor="ti-kind">Type</Label>
          <select id="ti-kind" className={select} value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
            <option value="carryovers">Carryover from the prior year</option>
            <option value="other">Other item</option>
          </select>
        </div>
        {kind === "carryovers" ? (
          <div>
            <Label htmlFor="ti-code">Carryover</Label>
            <select id="ti-code" className={select} value={code} onChange={(e) => setCode(e.target.value)}>
              {items.map((i) => (
                <option key={i.code} value={i.code}>
                  {i.label}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div>
            <Label htmlFor="ti-label">Description</Label>
            <Input id="ti-label" value={label} maxLength={200} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Gambling winnings (Form W-2G)" />
          </div>
        )}
        <div>
          <Label htmlFor="ti-value">Amount or value</Label>
          <Input id="ti-value" value={value} maxLength={500} onChange={(e) => setValue(e.target.value)} placeholder="e.g. (3,000.00)" />
        </div>
        <div>
          <Label htmlFor="ti-note">Note (optional)</Label>
          <Input id="ti-note" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} placeholder="e.g. From the 2025 Schedule D" />
        </div>
        {add.error && <p className="text-sm text-destructive">{errorMessage(add.error)}</p>}
        <Button type="submit" size="sm" disabled={add.isPending || !value.trim() || (kind === "other" && !label.trim())}>
          <Plus className="mr-1.5 h-4 w-4" /> Add
        </Button>
      </form>
    </Section>
  );
}
